'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface CaseStudy {
  id: number;
  title: string;
  persona: string;
  age: number;
  situation: string;
  challenge: string;
  solution: string;
  results: string[];
  timeframe: string;
  icon: string;
}

const CaseStudiesSection = () => {
  const [activeCase, setActiveCase] = useState<number>(1);

  const caseStudies: CaseStudy[] = [
    {
      id: 1,
      title: 'Od chaosu k jasné strategii',
      persona: 'Mladá rodina',
      age: 32,
      situation: 'Manželé s malým dítětem, oba pracující, plánují koupi bytu v Praze',
      challenge: 'Neměli přehled o financích, nevěděli, kolik si mohou dovolit na hypotéku, žádné úspory ani pojištění',
      solution: 'Vytvořili jsme komplexní finanční plán s důrazem na krátkodobé cíle (hypotéka) i dlouhodobé zabezpečení rodiny',
      results: [
        'Za 6 měsíců našetřili 300 000 Kč na vlastní zdroje',
        'Získali hypotéku s výhodnou sazbou 4,2%',
        'Nastavili automatické investování 5 000 Kč měsíčně',
        'Optimalizovali pojištění a ušetřili 8 000 Kč ročně',
      ],
      timeframe: '18 měsíců spolupráce',
      icon: 'HomeIcon',
    },
    {
      id: 2,
      title: 'Přechod na novou životní etapu',
      persona: 'Profesionál ve středním věku',
      age: 45,
      situation: 'Úspěšný manažer po rozvodu, potřeboval přeorganizovat finance a zajistit budoucnost',
      challenge: 'Změna rodinné situace, nejasná budoucnost, potřeba nového finančního plánu',
      solution: 'Postupně jsme vybudovali novou strategii s důrazem na flexibilitu a dlouhodobé cíle',
      results: [
        'Restrukturalizace portfolia v hodnotě 2,5 mil. Kč',
        'Nastavení penzijního připojištění s příspěvkem 3 000 Kč měsíčně',
        'Vytvoření rezervního fondu 6 měsíčních výdajů',
        'Plán na předčasný důchod v 60 letech',
      ],
      timeframe: '24 měsíců spolupráce',
      icon: 'UserIcon',
    },
    {
      id: 3,
      title: 'Růst s měnící se kariérou',
      persona: 'Začínající podnikatel',
      age: 28,
      situation: 'Přechod z korporátu na vlastní byznys, nepravidelné příjmy, potřeba stability',
      challenge: 'Nejistota příjmů, potřeba finančního polštáře, plánování investic do firmy',
      solution: 'Vytvořili jsme flexibilní plán, který se přizpůsobuje měnícím se příjmům a potřebám podnikání',
      results: [
        'Vybudování rezervy pokrývající 12 měsíců výdajů',
        'Optimalizace daní a sociálního pojištění',
        'Investiční strategie s měsíčními vklady 2 000-10 000 Kč',
        'Plán na koupi nemovitosti do 5 let',
      ],
      timeframe: '12 měsíců spolupráce',
      icon: 'BriefcaseIcon',
    },
  ];

  const activeStudy = caseStudies.find((study) => study.id === activeCase);

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4 font-headline">
              Příběhy našich klientů
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Anonymizované případové studie ukazují, jak živý finanční plán pomohl reálným lidem v různých životních situacích
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {caseStudies.map((study) => {
              const isActive = activeCase === study.id;
              
              return (
                <button
                  key={study.id}
                  onClick={() => setActiveCase(study.id)}
                  className={`p-6 rounded-xl border-2 transition-smooth text-left ${
                    isActive
                      ? 'bg-primary text-white border-primary shadow-elevated'
                      : 'bg-card text-text-primary border-border hover:border-primary/50 hover:shadow-card'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-lg ${isActive ? 'bg-white/20' : 'bg-primary/10'} flex items-center justify-center mb-4`}>
                    <Icon
                      name={study.icon as any}
                      size={24}
                      className={isActive ? 'text-white' : 'text-primary'}
                    />
                  </div>
                  <h3 className={`text-lg font-semibold mb-2 ${isActive ? 'text-white' : 'text-text-primary'}`}>
                    {study.title}
                  </h3>
                  <p className={`text-sm ${isActive ? 'text-white/80' : 'text-text-secondary'}`}>
                    {study.persona}, {study.age} let
                  </p>
                </button>
              );
            })}
          </div>

          {activeStudy && (
            <div className="bg-card rounded-xl shadow-elevated overflow-hidden">
              <div className="bg-gradient-to-r from-primary to-secondary p-6 lg:p-8 text-white">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <Icon name={activeStudy.icon as any} size={32} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2 font-headline">{activeStudy.title}</h3>
                    <p className="text-white/90">
                      {activeStudy.persona}, {activeStudy.age} let
                    </p>
                  </div>
                  <div className="hidden lg:block px-4 py-2 bg-white/20 rounded-lg text-sm font-medium">
                    {activeStudy.timeframe}
                  </div>
                </div>
              </div>

              <div className="p-6 lg:p-8 space-y-6">
                <div>
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="UserCircleIcon" size={20} className="text-primary" />
                    <h4 className="font-semibold text-text-primary">Výchozí situace</h4>
                  </div>
                  <p className="text-text-secondary leading-relaxed">{activeStudy.situation}</p>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="ExclamationTriangleIcon" size={20} className="text-warning" />
                    <h4 className="font-semibold text-text-primary">Výzva</h4>
                  </div>
                  <p className="text-text-secondary leading-relaxed">{activeStudy.challenge}</p>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="flex items-center space-x-2 mb-3">
                    <Icon name="LightBulbIcon" size={20} className="text-accent" />
                    <h4 className="font-semibold text-text-primary">Naše řešení</h4>
                  </div>
                  <p className="text-text-secondary leading-relaxed">{activeStudy.solution}</p>
                </div>

                <div className="border-t border-border pt-6">
                  <div className="flex items-center space-x-2 mb-4">
                    <Icon name="CheckBadgeIcon" size={20} className="text-success" />
                    <h4 className="font-semibold text-text-primary">Dosažené výsledky</h4>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {activeStudy.results.map((result, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-success/5 rounded-lg border border-success/20">
                        <Icon name="CheckCircleIcon" size={20} className="text-success flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-text-primary">{result}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="lg:hidden pt-4 border-t border-border">
                  <div className="flex items-center justify-center space-x-2 text-text-secondary">
                    <Icon name="ClockIcon" size={16} className="text-current" />
                    <span className="text-sm font-medium">{activeStudy.timeframe}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="mt-8 p-6 bg-muted rounded-lg border border-border">
            <div className="flex items-start space-x-3">
              <Icon name="ShieldCheckIcon" size={24} className="text-primary flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-text-primary mb-2">Ochrana soukromí</h4>
                <p className="text-sm text-text-secondary leading-relaxed">
                  Všechny případové studie jsou anonymizované a upravené pro ochranu soukromí našich klientů. Konkrétní čísla a detaily byly změněny, ale celkový příběh a dosažené výsledky odpovídají realitě.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;